
import React, { useEffect, useState } from 'react';
import { Button } from './button';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { TutorialStep } from '@/hooks/use-tutorial';

interface TutorialOverlayProps {
  step: TutorialStep;
  isFirstStep: boolean;
  isLastStep: boolean;
  currentStepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onComplete: () => void;
}

export function TutorialOverlay({
  step,
  isFirstStep,
  isLastStep,
  currentStepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  onComplete
}: TutorialOverlayProps) {
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [overlayPosition, setOverlayPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    console.log('Looking for element:', step.target);
    const element = document.querySelector(step.target) as HTMLElement;
    console.log('Found element:', element);
    setTargetElement(element);

    if (element) {
      const rect = element.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

      let top = 0;
      let left = 0;

      // Ensure the tutorial popup stays within viewport bounds
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const popupWidth = 320; // Approximate popup width
      const popupHeight = 200; // Approximate popup height

      switch (step.position) {
        case 'top':
          top = Math.max(20, rect.top + scrollTop - popupHeight - 20);
          left = Math.min(viewportWidth - popupWidth - 20, Math.max(20, rect.left + scrollLeft + rect.width / 2 - popupWidth / 2));
          break;
        case 'bottom':
          top = Math.min(viewportHeight - popupHeight - 20, rect.bottom + scrollTop + 20);
          left = Math.min(viewportWidth - popupWidth - 20, Math.max(20, rect.left + scrollLeft + rect.width / 2 - popupWidth / 2));
          break;
        case 'left':
          top = Math.min(viewportHeight - popupHeight - 20, Math.max(20, rect.top + scrollTop + rect.height / 2 - popupHeight / 2));
          left = Math.max(20, rect.left + scrollLeft - popupWidth - 20);
          break;
        case 'right':
          top = Math.min(viewportHeight - popupHeight - 20, Math.max(20, rect.top + scrollTop + rect.height / 2 - popupHeight / 2));
          left = Math.min(viewportWidth - popupWidth - 20, rect.right + scrollLeft + 20);
          break;
      }

      setOverlayPosition({ top, left });

      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [step]);

  // If no target element found, show a fallback position
  if (!targetElement) {
    console.warn('Target element not found for tutorial step:', step.target);
    // Show tutorial in center of screen as fallback
    return (
      <>
        <div 
          className="fixed inset-0 bg-black/40 z-[9998]" 
          onClick={onSkip}
        />
        
        <div 
          className="fixed z-[10000] bg-white rounded-lg shadow-xl p-4 md:p-6 w-80 max-w-[90vw] animate-fade-in top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        >
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 pr-2 flex-1">{step.title}</h3>
            <button 
              onClick={onSkip} 
              className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <p className="text-sm md:text-base text-gray-600 mb-6 leading-relaxed">{step.content}</p>
          
          <div className="flex justify-between items-center gap-2">
            <div className="flex space-x-1">
              {Array.from({ length: totalSteps }).map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full ${
                    index === currentStepIndex ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
            
            <div className="flex space-x-2">
              {!isFirstStep && (
                <Button variant="outline" size="sm" onClick={onPrev} className="text-xs md:text-sm">
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              )}
              <Button 
                size="sm" 
                onClick={isLastStep ? onComplete : onNext}
                className="bg-blue-600 hover:bg-blue-700 text-xs md:text-sm"
              >
                {isLastStep ? 'Complete' : 'Next'}
                {!isLastStep && <ChevronRight className="h-4 w-4 ml-1" />}
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Dark overlay with proper z-index */}
      <div 
        className="fixed inset-0 bg-black/40 z-[9998]" 
        onClick={onSkip}
      />
      
      {/* Highlight box */}
      <div 
        className="fixed border-2 border-blue-500 bg-white/10 rounded-lg z-[9999] pointer-events-none"
        style={{
          top: targetElement.getBoundingClientRect().top + window.pageYOffset - 4,
          left: targetElement.getBoundingClientRect().left + window.pageXOffset - 4,
          width: targetElement.offsetWidth + 8,
          height: targetElement.offsetHeight + 8,
        }}
      />

      {/* Tutorial popup */}
      <div 
        className="fixed z-[10000] bg-white rounded-lg shadow-xl p-4 md:p-6 w-80 max-w-[90vw] animate-fade-in"
        style={{
          top: overlayPosition.top,
          left: overlayPosition.left,
        }}
      >
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-base md:text-lg font-semibold text-gray-900 pr-2 flex-1">{step.title}</h3>
          <button 
            onClick={onSkip} 
            className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <p className="text-sm md:text-base text-gray-600 mb-6 leading-relaxed">{step.content}</p>
        
        <div className="flex justify-between items-center gap-2">
          <div className="flex space-x-1">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index === currentStepIndex ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          
          <div className="flex space-x-2">
            {!isFirstStep && (
              <Button variant="outline" size="sm" onClick={onPrev} className="text-xs md:text-sm">
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
            <Button 
              size="sm" 
              onClick={isLastStep ? onComplete : onNext}
              className="bg-blue-600 hover:bg-blue-700 text-xs md:text-sm"
            >
              {isLastStep ? 'Complete' : 'Next'}
              {!isLastStep && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
